import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';

// Fabric-master typeahead for BOTH chains (RSR GPO + Deroi estimate).
// Reads /oe/fabric-master (server excludes MERGED rows; PENDING rows ARE selectable and
// carry approval_status so we chip them "pending approval"). Request-and-proceed: when the
// needed quality is absent, the user creates it inline (POST /oe/fabric-master/request) and
// continues immediately — it enters as 'pending' and never blocks. Design ruling 9.
//
// Props:
//   value        — the selected fabric_master_id (or '')
//   initialLabel — display name when editing an existing record (optional)
//   onChange(id, fabricObj)  — fires on select / inline-create / clear
//   placeholder, style, disabled
const box = { padding: '6px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', width: '100%' };
const chip = (bg, fg) => ({ background: bg, color: fg, borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700, marginLeft: 6, whiteSpace: 'nowrap' });

export default function FabricTypeahead({ value, initialLabel, onChange, placeholder, style, disabled }) {
  const [q, setQ] = useState(initialLabel || '');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(value ? { id: value, fabric_name: initialLabel } : null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrap = useRef(null);
  const timer = useRef(null);

  useEffect(() => { setQ(initialLabel || ''); setSelected(value ? { id: value, fabric_name: initialLabel } : null); }, [value, initialLabel]);

  // close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const search = (text) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.get('/oe/fabric-master', { params: text ? { search: text } : {} })
        .then(r => setRows(r.data.data || []))
        .catch(() => setRows([]));
    }, 200);
  };

  const onType = (text) => {
    setQ(text); setOpen(true); setSelected(null);
    if (value) onChange('', null);   // typing clears a prior selection
    search(text);
  };

  const pick = (f) => {
    setSelected(f); setQ(f.fabric_name); setOpen(false);
    onChange(f.id, f);
  };

  // request-and-proceed: create a pending fabric from the typed text, then select it.
  const requestNew = () => {
    const name = q.trim();
    if (!name || busy) return;
    setBusy(true);
    api.post('/oe/fabric-master/request', { fabric_name: name })
      .then(r => { pick(r.data.data); setCreating(false); })
      .catch(e => window.alert(e.response?.data?.error || 'Could not create fabric'))
      .finally(() => setBusy(false));
  };

  const clearSel = () => { setSelected(null); setQ(''); setRows([]); onChange('', null); };

  const exactExists = rows.some(r => (r.fabric_name || '').trim().toLowerCase() === q.trim().toLowerCase());

  return (
    <div ref={wrap} style={{ position: 'relative', ...style }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <input
          value={q} disabled={disabled} placeholder={placeholder || 'Search fabric…'}
          onChange={e => onType(e.target.value)} onFocus={() => { setOpen(true); if (!rows.length) search(q); }}
          style={{ ...box, borderColor: selected ? (selected.approval_status === 'pending' ? '#B45309' : '#16a34a') : '#ddd' }} />
        {selected && !disabled && (
          <button type="button" onClick={clearSel} title="Clear"
            style={{ border: 'none', background: 'none', color: '#A32D2D', cursor: 'pointer', fontSize: 16, marginLeft: 4 }}>×</button>
        )}
      </div>
      {selected && selected.approval_status === 'pending' && (
        <div style={{ marginTop: 3 }}><span style={chip('#FEF3C7', '#B45309')}>pending approval</span></div>
      )}
      {open && !disabled && (
        <div style={{ position: 'absolute', zIndex: 40, top: '100%', left: 0, right: 0, background: '#fff',
          border: '1px solid #ddd', borderRadius: 6, marginTop: 2, maxHeight: 240, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          {rows.map(f => (
            <div key={f.id} onClick={() => pick(f)}
              style={{ padding: '7px 10px', cursor: 'pointer', borderBottom: '1px solid #f2f2f2', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              onMouseEnter={e => e.currentTarget.style.background = '#F7F7FB'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
              <span style={{ fontSize: 13 }}>
                {f.fabric_name}
                {f.fabric_code && <span style={{ color: '#999', fontSize: 11, marginLeft: 6 }}>{f.fabric_code}</span>}
              </span>
              {f.approval_status === 'pending' && <span style={chip('#FEF3C7', '#B45309')}>pending</span>}
            </div>
          ))}
          {q.trim() && !exactExists && (
            <div onClick={requestNew}
              style={{ padding: '8px 10px', cursor: 'pointer', color: '#3B3A8F', fontWeight: 700, fontSize: 13, background: '#EEEDF8' }}>
              ＋ Create “{q.trim()}” and proceed <span style={{ fontWeight: 500, color: '#777' }}>(enters as pending)</span>
            </div>
          )}
          {!rows.length && !q.trim() && (
            <div style={{ padding: '8px 10px', color: '#999', fontSize: 12 }}>Type to search the fabric master…</div>
          )}
        </div>
      )}
    </div>
  );
}
